import React, { useState, useEffect } from 'react';

/**
 * トーストコンポーネント
 * @param {Object} props - プロパティ
 * @param {boolean} props.isVisible - トーストの表示/非表示
 * @param {string} props.message - 表示するメッセージ
 * @param {string} props.type - トーストの種類 ('success', 'error', 'warning', 'info')
 * @param {number} props.duration - 自動非表示までの時間（ミリ秒）
 * @param {Function} props.onClose - トーストを閉じる時のコールバック
 * @param {string} props.position - 表示位置 ('top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center')
 */
const Toast = ({
  isVisible = false,
  message = '',
  type = 'info',
  duration = 5000,
  onClose,
  position = 'top-right',
  customStyles = {},
  showIcon = false 
}) => {
  const [isShowing, setIsShowing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // 表示アニメーション
      setIsShowing(true);
      setTimeout(() => setIsAnimating(true), 10);

      // 自動非表示タイマー
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
    // handleClose を依存に含めると毎描画でタイマーを張り直すため除外する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, duration]);

  const handleClose = () => {
    // 非表示アニメーション
    setIsAnimating(false);
    setTimeout(() => {
      setIsShowing(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!isShowing) return null;

  // トーストの種類に応じたスタイル
  const getTypeStyles = () => {
    const baseStyles = {
      success: {
        backgroundColor: '#d4edda',
        borderColor: '#c3e6cb',
        color: '#155724',
        icon: '✓'
      },
      error: {
        backgroundColor: '#f8d7da',
        borderColor: '#f5c6cb',
        color: '#721c24',
        icon: '✗'
      },
      warning: {
        backgroundColor: '#fff3cd',
        borderColor: '#ffeaa7',
        color: '#856404',
        icon: '⚠'
      },
      info: {
        backgroundColor: '#d1ecf1',
        borderColor: '#bee5eb',
        color: '#0c5460',
        icon: 'ℹ'
      }
    };
    return baseStyles[type] || baseStyles.info;
  };

  // 表示位置に応じたスタイル
  const getPositionStyles = () => {
    const positions = {
      'top-right': {
        top: '20px',
        right: '20px'
      },
      'top-left': {
        top: '20px',
        left: '20px'
      },
      'bottom-right': {
        bottom: '20px',
        right: '20px'
      },
      'bottom-left': {
        bottom: '20px',
        left: '20px'
      },
      'top-center': {
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)'
      },
      'bottom-center': {
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)'
      }
    };
    return positions[position] || positions['top-right'];
  };

  const typeStyles = getTypeStyles();
  const positionStyles = getPositionStyles();

  const styles = {
    container: {
      position: 'fixed',
      zIndex: customStyles.zIndex || 9999,
      minWidth: customStyles.minWidth || '300px',
      maxWidth: customStyles.maxWidth || '500px',
      padding: '16px 20px',
      borderRadius: '8px',
      border: `1px solid ${typeStyles.borderColor}`,
      backgroundColor: typeStyles.backgroundColor,
      color: typeStyles.color,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: showIcon ? '12px' : '0px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5',
      opacity: isAnimating ? 1 : 0,
      transform: position.includes('top') 
        ? `translateY(${isAnimating ? '0' : '-20px'})` 
        : `translateY(${isAnimating ? '0' : '20px'})`,
      transition: 'all 0.3s ease-in-out',
      ...positionStyles,
      ...customStyles
    },
    icon: {
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0,
      marginTop: '1px'
    },
    content: {
      flex: 1,
      wordBreak: 'break-word'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: typeStyles.color,
      fontSize: '18px',
      cursor: 'pointer',
      padding: '0',
      marginLeft: '8px',
      flexShrink: 0,
      opacity: 0.7,
      transition: 'opacity 0.2s ease'
    },
    progressBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: '3px',
      backgroundColor: typeStyles.color,
      opacity: 0.3,
      animation: `toast-progress ${duration}ms linear forwards`,
      borderRadius: '0 0 8px 8px'
    }
  };

  return (
    <>
      {/* プログレスバーのアニメーション用CSS */}
      <style>
        {`
          @keyframes toast-progress {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}
      </style>
      
      <div style={styles.container}>
        {/* アイコンを条件付きで表示 */}
        {showIcon && (
          <div style={styles.icon}>
            {typeStyles.icon}
          </div>
        )}
        <div style={styles.content}>
          {message}
        </div>
        <div style={styles.progressBar} />
      </div>
    </>
  );
};

export default Toast;